import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

/**
 * OnboardingPage — shown on first login only.
 * Collects the fields that admin couldn't fill at member creation time.
 * Once submitted, profile_complete = true and they never see this again.
 */
export default function OnboardingPage() {
  const { member, refreshMember } = useAuth();
  const [step, setStep] = useState(1); // 1 = personal, 2 = emergency, 3 = cricket
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    mobile: member?.mobile || '',
    dob: '',
    anniversary: '',
    is_married: false,
    emergency_name: '',
    emergency_mobile: '',
    emergency_relation: '',
    cricheroes_name: '',
  });

  function update(key, val) {
    setForm(f => ({ ...f, [key]: val }));
  }

  async function handleSubmit() {
    // Validate required fields
    if (!form.mobile) return toast.error('Mobile number is required');
    if (form.mobile.replace(/\D/g, '').length < 10) return toast.error('Enter a valid 10-digit mobile number');
    if (!form.dob) return toast.error('Date of birth is required');
    if (!form.emergency_name || !form.emergency_mobile) return toast.error('Emergency contact name and number are required');

    setSaving(true);
    try {
      await api.put(`/members/${member.id}`, {
        mobile: form.mobile.replace(/\D/g, ''),
        dob: form.dob,
        anniversary: form.is_married && form.anniversary ? form.anniversary : null,
        emergency_name: form.emergency_name,
        emergency_mobile: form.emergency_mobile.replace(/\D/g, ''),
        emergency_relation: form.emergency_relation,
        cricheroes_name: form.cricheroes_name || null,
        profile_complete: true,
      });
      toast.success('Welcome to UCC Platform! 🏏');
      await refreshMember(); // updates member in context → profile_complete = true → gate lifts
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const STEP_LABELS = ['Personal', 'Emergency', 'Cricket'];
  const totalSteps = 3;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--ucc-gray-100)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        background: 'var(--ucc-navy-dark)',
        padding: 'var(--space-lg) var(--space-md) var(--space-md)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🏏</div>
        <h1 style={{ fontFamily: 'Rajdhani,sans-serif', color: '#F5C518', fontSize: '1.6rem', fontWeight: 900, marginBottom: 4 }}>
          WELCOME, {member?.display_name?.split(' ')[0]?.toUpperCase()}!
        </h1>
        <p style={{ color: 'rgba(255,255,255,.65)', fontSize: '0.85rem', marginBottom: 'var(--space-md)' }}>
          Quick one-time setup — takes 60 seconds
        </p>

        {/* Step indicator */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
          {STEP_LABELS.map((label, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: i + 1 < step ? 'var(--success)' : i + 1 === step ? '#F5C518' : 'rgba(255,255,255,.2)',
                color: i + 1 === step ? '#1E3A6E' : 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '0.85rem', fontFamily: 'Rajdhani,sans-serif',
                transition: 'all 0.2s',
              }}>
                {i + 1 < step ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: '0.65rem', color: i + 1 <= step ? '#F5C518' : 'rgba(255,255,255,.4)', fontWeight: i + 1 === step ? 700 : 400, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="container" style={{ paddingTop: 'var(--space-lg)', flex: 1, maxWidth: 480 }}>

        {/* ── Step 1: Personal ── */}
        {step === 1 && (
          <div className="card">
            <div className="card__header">
              <span className="card__title">📋 Personal Details</span>
            </div>
            <div className="card__body">
              <div className="form-group">
                <label className="form-label">Mobile Number *</label>
                <input
                  type="tel"
                  className="form-control"
                  placeholder="10-digit mobile number"
                  value={form.mobile}
                  onChange={e => update('mobile', e.target.value)}
                  inputMode="tel"
                  maxLength={13}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)', marginTop: 4 }}>
                  Used for match day communications
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Date of Birth *</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.dob}
                  onChange={e => update('dob', e.target.value)}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0]}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)', marginTop: 4 }}>
                  🎂 The club will celebrate your birthday!
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Married?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['Yes', true], ['No', false]].map(([label, val]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => update('is_married', val)}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 'var(--radius-md)',
                        border: `2px solid ${form.is_married === val ? '#1E3A6E' : 'var(--ucc-gray-200)'}`,
                        background: form.is_married === val ? '#1E3A6E' : 'white',
                        color: form.is_married === val ? 'white' : 'var(--ucc-gray-700)',
                        fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, cursor: 'pointer',
                        fontSize: '0.95rem', transition: 'all 0.15s',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {form.is_married && (
                <div className="form-group">
                  <label className="form-label">Wedding Anniversary</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.anniversary}
                    onChange={e => update('anniversary', e.target.value)}
                  />
                  <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)', marginTop: 4 }}>
                    💍 The club will celebrate it too!
                  </div>
                </div>
              )}

              <button
                className="btn btn-primary btn-block"
                style={{ marginTop: 'var(--space-md)' }}
                onClick={() => {
                  if (!form.mobile || form.mobile.replace(/\D/g, '').length < 10) return toast.error('Valid mobile number required');
                  if (!form.dob) return toast.error('Date of birth required');
                  setStep(2);
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Emergency Contact ── */}
        {step === 2 && (
          <div className="card">
            <div className="card__header">
              <span className="card__title">🆘 Emergency Contact</span>
            </div>
            <div className="card__body">
              <div className="alert alert-info mb-md">
                <span>ℹ️</span>
                <div style={{ fontSize: '0.82rem' }}>
                  Required for match day safety. This person will be contacted if you need medical help on the ground.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Contact Name *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Priya Sharma"
                  value={form.emergency_name}
                  onChange={e => update('emergency_name', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Contact Mobile *</label>
                <input
                  type="tel"
                  className="form-control"
                  placeholder="10-digit mobile number"
                  value={form.emergency_mobile}
                  onChange={e => update('emergency_mobile', e.target.value)}
                  inputMode="tel"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Relation</label>
                <select
                  className="form-control"
                  value={form.emergency_relation}
                  onChange={e => update('emergency_relation', e.target.value)}
                >
                  <option value="">Select relation…</option>
                  {['Spouse', 'Father', 'Mother', 'Brother', 'Sister', 'Son', 'Daughter', 'Friend', 'Other'].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 'var(--space-md)' }}>
                <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setStep(1)}>← Back</button>
                <button
                  className="btn btn-primary"
                  style={{ flex: 2 }}
                  onClick={() => {
                    if (!form.emergency_name.trim()) return toast.error('Contact name required');
                    if (!form.emergency_mobile || form.emergency_mobile.replace(/\D/g, '').length < 10) return toast.error('Valid emergency mobile required');
                    setStep(3);
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Cricket Details ── */}
        {step === 3 && (
          <div className="card">
            <div className="card__header">
              <span className="card__title">🏏 Cricket Details</span>
            </div>
            <div className="card__body">
              <div className="form-group">
                <label className="form-label">Your CricHeroes Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Exactly as it appears on CricHeroes"
                  value={form.cricheroes_name}
                  onChange={e => update('cricheroes_name', e.target.value)}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)', marginTop: 4 }}>
                  Used to match your name on scorecards. Leave blank if you don't play on CricHeroes.
                </div>
              </div>

              {/* Summary before submit */}
              <div style={{
                background: 'var(--ucc-gray-100)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-md)',
                marginBottom: 'var(--space-md)',
                fontSize: '0.82rem',
              }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: '#1E3A6E' }}>✅ Your Details Summary</div>
                {[
                  ['Mobile', form.mobile],
                  ['Date of Birth', form.dob ? new Date(form.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'],
                  ['Anniversary', form.is_married && form.anniversary ? new Date(form.anniversary).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : form.is_married ? 'Not provided' : 'N/A'],
                  ['Emergency Contact', form.emergency_name + (form.emergency_relation ? ` (${form.emergency_relation})` : '')],
                  ['Emergency Mobile', form.emergency_mobile],
                  ['CricHeroes Name', form.cricheroes_name || 'Not provided'],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid var(--ucc-gray-200)' }}>
                    <span style={{ color: 'var(--ucc-gray-500)' }}>{label}</span>
                    <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setStep(2)}>← Back</button>
                <button
                  className="btn btn-gold"
                  style={{ flex: 2 }}
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? '⏳ Saving…' : '🏏 All Done!'}
                </button>
              </div>

              <div style={{ fontSize: '0.72rem', color: 'var(--ucc-gray-400)', textAlign: 'center', marginTop: 12 }}>
                You can update any of these later from your Profile page
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
